import { useSelector } from 'react-redux';
import { State } from 'redux/initialState';
import { SocietyCorporateId } from 'types/Society';

import { usePartnerData } from './usePartnerData';
import { usePartnerInversion } from './usePartnerInversion';
import { usePartnerObservations } from './usePartnerObservations';
import { usePartnerMovements } from './usePartnerMovements';
import { useSocietyInitialization } from './useSocietyInitialization';

type PartnerDetailProps = {
  partnerId: string;
  societyId: string;
};

export const usePartnerDetail = ({
  partnerId,
  societyId,
}: PartnerDetailProps) => {
  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );

  const isAxaSociety = actualSociety?.corporateId === SocietyCorporateId.AXA;

  const { isLoading: isSocietyLoading, isError: isSocietyError } =
    useSocietyInitialization(societyId, actualSociety);

  const {
    partner,
    incorporationDate,
    fetchPartnerData,
    isLoading: isPartnerLoading,
    isError: isPartnerError,
  } = usePartnerData(partnerId, actualSociety);

  const {
    inversion,
    isLoading: isInversionLoading,
    isError: isInversionError,
  } = usePartnerInversion(partnerId, societyId);

  const {
    observations,
    isLoading: isObservationsLoading,
    isError: isObservationsError,
  } = usePartnerObservations(partnerId, societyId);

  const {
    customMovements,
    isLoading: isMovementsLoading,
    isError: isMovementsError,
  } = usePartnerMovements(partnerId, societyId, isAxaSociety);

  const isLoading =
    isSocietyLoading ||
    isPartnerLoading ||
    isInversionLoading ||
    isObservationsLoading ||
    isMovementsLoading;

  const isError =
    isSocietyError ||
    isPartnerError ||
    isInversionError ||
    isObservationsError ||
    isMovementsError;

  return {
    data: {
      partner,
      incorporationDate,
      inversion,
      observations,
      customMovements,
    },
    actions: {
      fetchPartnerData,
    },
    isLoading,
    isError,
  };
};
